import type { IMarket } from "~/types/event";
import { getDaysRemaining } from "~/utils/formatDateCounter";
import { calcNoPrice } from "~/utils/formatPrice";

export interface ICalculatedData {
  entryPrice: number | null;
  shares: number;
  impliedChance: number;
  grossPayout: number;
  netProfit: number;
  roi: number;
  apy: number;
  multiplier: number;
  daysRemaining: number;
}

export const EMPTY_CALCULATED_DATA: ICalculatedData = {
  entryPrice: null,
  shares: 0,
  impliedChance: 0,
  grossPayout: 0,
  netProfit: 0,
  roi: 0,
  apy: 0,
  multiplier: 0,
  daysRemaining: 0,
};

const hasBackendCalculatorMetrics = (
  market: IMarket,
  answer: boolean
): market is IMarket & { roi: number; apy: number } =>
  answer &&
  market.roi != null &&
  market.apy != null &&
  Number.isFinite(market.roi) &&
  Number.isFinite(market.apy);

const toUsablePrice = (price: number | null | undefined): number | null =>
  typeof price === "number" && Number.isFinite(price) && price > 0 && price < 1
    ? price
    : null;

export const resolveEntryPrice = (
  market: IMarket,
  answer: boolean
): number | null => {
  const yesEntry =
    toUsablePrice(market.bestAsk) ?? toUsablePrice(market.lastTradePrice);
  const noEntry = toUsablePrice(market.priceNo);

  return answer
    ? (yesEntry ?? calcNoPrice(noEntry))
    : (noEntry ?? calcNoPrice(yesEntry));
};

const computeReturns = (
  netProfit: number,
  betAmount: number,
  daysRemaining: number
) => {
  const roiBase = netProfit / betAmount;

  return {
    roi: roiBase * 100,
    apy:
      daysRemaining > 0
        ? (Math.pow(1 + roiBase, 365 / daysRemaining) - 1) * 100
        : 0,
  };
};

export const computeMarketCalculatedData = (
  market: IMarket,
  answer: boolean,
  betAmount: number
): ICalculatedData => {
  const entryPrice = resolveEntryPrice(market, answer);
  const yesChance = Math.round((market.chance || 0) * 100);
  const impliedChance = answer ? yesChance : 100 - yesChance;
  const daysRemaining = getDaysRemaining(market.endDate);

  if (entryPrice === null) {
    return { ...EMPTY_CALCULATED_DATA, impliedChance, daysRemaining };
  }

  const shares = betAmount / entryPrice;
  const netProfit = shares - betAmount;
  const { roi, apy } = hasBackendCalculatorMetrics(market, answer)
    ? { roi: market.roi, apy: market.apy }
    : computeReturns(netProfit, betAmount, daysRemaining);

  return {
    entryPrice,
    impliedChance,
    daysRemaining,
    shares,
    grossPayout: shares,
    netProfit,
    roi,
    apy,
    multiplier: 1 / entryPrice,
  };
};
